import type { ReactNode } from "react"

import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/clientWeb/components/ui/tooltip"
import { formatOccurrenceDay } from "../utils/recurrence"

interface JoinLockHintProps {
  /** From `joinLockedUntil`: the occurrence's start, or null when it can be joined. */
  lockedUntilMs: number | null
  /** The (disabled, when locked) Join / Start button. */
  children: ReactNode
}

/**
 * Says when a future day of a series opens, over its disabled Join button.
 *
 * A disabled button gets no pointer or focus events, so the tooltip hangs off
 * a focusable wrapper instead.
 */
export function JoinLockHint({ lockedUntilMs, children }: JoinLockHintProps) {
  const { t, language } = useLanguage()
  if (lockedUntilMs === null) return <>{children}</>

  const hint = t("meetings.series.joinOpensOn", { date: formatOccurrenceDay(lockedUntilMs, language) })
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={hint}
          className="inline-flex cursor-not-allowed rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{hint}</TooltipContent>
    </Tooltip>
  )
}
